import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';

import { escapeHtml } from '@core/utils/html-escape.utils';

import {
  PillItem,
  PillsOverflowComponent
} from '@shared/components/pills-overflow/pills-overflow.component';
import {
  formatCellValue,
  processUidListValue
} from '@shared/components/table/table-cell/utils';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { ReferencePopupService } from '@shared/services/reference-popup/reference-popup.service';

import { CellOverlayAction } from '../../actions-overlay/actions-overlay.component';
import { UidLabelResolver } from '../../uid-label-resolver';
import { UidListItem, isUidListItem } from '../uid-list-item.model';

@Component({
  selector: 'app-uid-list-cell',
  imports: [NgTemplateOutlet, PillsOverflowComponent, TooltipComponent],
  templateUrl: './uid-list-cell.component.html'
})
export class UidListCellComponent {
  private readonly referencePopupService = inject(ReferencePopupService);

  availableCellActions: CellOverlayAction[] = ['copy', 'filter'];
  availableCellShiftActions: CellOverlayAction[] = ['copy_raw'];
  valueAsString = (v: unknown) => formatCellValue(v, 'uid-list');

  value = input<unknown | undefined>(undefined);
  displayLabelFor = input<UidLabelResolver | undefined>(undefined);
  targetEntityType = input<string | undefined>(undefined);
  columnId = input<string | undefined>(undefined);
  row = input<unknown>(undefined);

  uidClick = output<string>();

  protected readonly hoveredPillUid = signal<string | undefined>(undefined);

  protected processedValue = computed<string[] | undefined>(() =>
    processUidListValue(this.value())
  );

  /** Icons and hover text of the decorated items, by uid; plain uids have none. */
  protected readonly decorations = computed(() => {
    const value = this.value();
    const items = Array.isArray(value) ? value.filter(isUidListItem) : [];
    return new Map(items.map((item) => [item.uid, item]));
  });

  protected decorationOf(item: PillItem): UidListItem | undefined {
    return this.decorations().get(item.data as string);
  }

  /** The tooltip renders its title and content as HTML. */
  protected tooltipHtml(tooltip: NonNullable<UidListItem['tooltip']>): {
    title: string;
    content: string;
  } {
    return {
      title: escapeHtml(tooltip.title),
      content: tooltip.lines.map(escapeHtml).join('<br>')
    };
  }

  protected pillItems = computed(() => {
    const values = this.processedValue();
    if (!values) return [];
    const resolver = this.displayLabelFor();
    // Pill `data` stays the uid (emitted on click); `label` is what is shown.
    return values.map((uid) => ({ id: uid, data: uid, label: resolver?.(uid) ?? uid }));
  });

  protected onPillHover(item: PillItem): void {
    this.hoveredPillUid.set(item.data as string);
  }

  protected onPillHoverEnd(): void {
    this.hoveredPillUid.set(undefined);
  }

  protected onPillClick(item: PillItem, event: MouseEvent): void {
    const uid = item.data as string;
    const targetEntityType = this.targetEntityType();
    if (targetEntityType) {
      this.referencePopupService.open(event.currentTarget as HTMLElement, {
        uid,
        targetEntityType,
        column: this.columnId() ?? '',
        row: this.row()
      });
      return;
    }
    this.uidClick.emit(uid);
  }

  filterValue(): string | undefined {
    return this.hoveredPillUid() ?? this.processedValue()?.[0];
  }
}
